import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core'
import * as echarts from 'echarts'
import { Subscription } from 'rxjs'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { Themes } from 'src/app/shared/enums/enums'

@Component({
  selector: 'ddc-stat-line-chart',
  templateUrl: './ddc-stat-line-chart.component.html',
  styleUrls: ['./ddc-stat-line-chart.component.scss'],
})
export class DdcStatLineChartComponent implements AfterViewInit, OnDestroy {
  @Input() title: string = ''
  @Input() data: { name: string; value: number[]; timeLabels: string[] } | null = null
  @Input() theme: 'light-theme' | 'dark-theme' | 'paper-theme' = 'light-theme'

  @ViewChild('chartContainer') chartContainer!: ElementRef

  private chartInstance: echarts.ECharts | null = null
  private themeSubscription: Subscription

  constructor(private themeService: ThemeService) {
    this.themeSubscription = this.themeService.theme$.subscribe(theme => {
      this.theme = theme as 'light-theme' | 'dark-theme' | 'paper-theme'
      this.updateChartTheme()
    })
  }

  ngAfterViewInit(): void {
    this.initChart()
  }

  ngOnDestroy(): void {
    this.themeSubscription.unsubscribe()
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
  }

  private initChart(): void {
    if (!this.chartContainer) return

    this.chartInstance = echarts.init(this.chartContainer.nativeElement)
    this.updateChartTheme()
  }

  private updateChartTheme(): void {
    if (!this.chartInstance || !this.data) return

    const themeColors = this.getThemeColors()

    const option: echarts.EChartsOption = {
      title: {
        text: this.title,
        left: 'center',
        textStyle: {
          color: themeColors.text,
          fontSize: 16,
          fontWeight: 'bold',
        },
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: themeColors.tooltipBg,
        borderColor: themeColors.tooltipBorder,
        textStyle: {
          color: themeColors.text,
        },
      },
      grid: {
        left: '10%',
        right: '5%',
        bottom: '15%',
        top: '20%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: this.data.timeLabels,
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: themeColors.axis,
          },
        },
        axisLabel: {
          color: themeColors.text,
        },
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: themeColors.grid,
          },
        },
        axisLabel: {
          color: themeColors.text,
        },
      },
      series: [
        {
          name: this.data.name,
          type: 'line',
          data: this.data.value,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: {
            color: themeColors.line,
          },
          lineStyle: {
            width: 3,
            color: themeColors.line,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                {
                  offset: 0,
                  color: themeColors.areaStart,
                },
                {
                  offset: 1,
                  color: themeColors.areaEnd,
                },
              ],
            },
          },
          emphasis: {
            focus: 'series',
            itemStyle: {
              shadowBlur: 10,
              shadowColor: themeColors.line,
            },
          },
        },
      ],
    }

    this.chartInstance.setOption(option, true)
  }

  private getThemeColors(): {
    text: string
    axis: string
    grid: string
    line: string
    areaStart: string
    areaEnd: string
    tooltipBg: string
    tooltipBorder: string
  } {
    switch (this.theme) {
      case Themes.DARK:
        return {
          text: '#e0e0e0',
          axis: '#666666',
          grid: '#444444',
          line: '#4caf84',
          areaStart: 'rgba(76, 175, 137, 0.4)',
          areaEnd: 'rgba(76, 175, 80, 0.05)',
          tooltipBg: '#2c2c2c',
          tooltipBorder: '#444444',
        }
      case Themes.PAPER:
        return {
          text: '#5d4037',
          axis: '#8d6e63',
          grid: '#d7ccc8',
          line: '#795548',
          areaStart: 'rgba(121, 85, 72, 0.3)',
          areaEnd: 'rgba(121, 85, 72, 0.05)',
          tooltipBg: '#efebe9',
          tooltipBorder: '#d7ccc8',
        }
      case Themes.LIGHT:
      default:
        return {
          text: '#333333',
          axis: '#cccccc',
          grid: '#eeeeee',
          line: '#2196f3',
          areaStart: 'rgba(33, 150, 243, 0.3)',
          areaEnd: 'rgba(33, 150, 243, 0.05)',
          tooltipBg: '#ffffff',
          tooltipBorder: '#e0e0e0',
        }
    }
  }
}
